/** New Customer Onboarding Intake wizard (Customer Management, Demo Mode only). Mirrors the
 * structure of the retailer onboarding wizard (`pages/orderly-for-distributors/Onboarding`) -
 * a tabbed, multi-step wizard with per-tab components, completed-tab tracking and validation -
 * but is entirely local/mocked: there is no backend for Customer Management yet, so "Create
 * customer" just appends to the in-memory seed data (`mocks/data/customerManagementData.ts`).
 * lazy: upgrade path is to swap the local `addCustomer` call for a real create-customer API call
 * once one exists, following the same pattern as `useNswagClient`/`updateRetailerOnboarding`. */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { CheckCircle, NavigateNext } from '@mui/icons-material';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import BackButton from '../../../components/shared/actions/BackButton';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { addCustomer, buildMilestoneCategories, generateCustomerId } from '../../../mocks/data/customerManagementData';
import { OnboardingIntakeTab } from './enums/OnboardingIntakeTab';
import { CustomerIntakeForm, initialCustomerIntakeForm } from './types/CustomerIntakeForm';
import { isCommercialComplianceValid, isCustomerProfileValid, isServiceRequirementsValid } from './utils/OnboardingIntakeValidations';
import OnboardingCustomerProfile from './components/OnboardingCustomerProfile';
import OnboardingServiceRequirements from './components/OnboardingServiceRequirements';
import OnboardingCommercialCompliance from './components/OnboardingCommercialCompliance';
import OnboardingReview from './components/OnboardingReview';

const TAB_ORDER = [
  OnboardingIntakeTab.CUSTOMER_PROFILE,
  OnboardingIntakeTab.SERVICE_REQUIREMENTS,
  OnboardingIntakeTab.COMMERCIAL_COMPLIANCE,
  OnboardingIntakeTab.REVIEW,
];

const OnboardingIntake = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<OnboardingIntakeTab>(OnboardingIntakeTab.CUSTOMER_PROFILE);
  const [form, setForm] = useState<CustomerIntakeForm>(initialCustomerIntakeForm);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = <K extends keyof CustomerIntakeForm>(field: K, value: CustomerIntakeForm[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const isTabComplete = (tab: OnboardingIntakeTab): boolean => {
    if (tab === OnboardingIntakeTab.CUSTOMER_PROFILE) return isCustomerProfileValid(form);
    if (tab === OnboardingIntakeTab.SERVICE_REQUIREMENTS) return isServiceRequirementsValid(form);
    if (tab === OnboardingIntakeTab.COMMERCIAL_COMPLIANCE) return isCommercialComplianceValid(form);
    return isCustomerProfileValid(form) && isServiceRequirementsValid(form) && isCommercialComplianceValid(form);
  };

  const allComplete = isCustomerProfileValid(form) && isServiceRequirementsValid(form) && isCommercialComplianceValid(form);

  const handleCreateCustomer = () => {
    const id = generateCustomerId(form.name);
    addCustomer({
      id,
      name: form.name,
      sector: form.sector,
      operatingModel: form.operatingModel,
      owner: form.owner,
      sitesCount: typeof form.sitesCount === 'number' ? form.sitesCount : 0,
      status: 'Onboarding',
      onboardingStatus: 'NotStarted',
      commercialReadiness: 'NotStarted',
      journeyStage: 'ContractWon',
      onboardingMilestones: buildMilestoneCategories({
        commercialSetup: [true, true, false, false, false],
      }, false),
      riskLevel: 'Medium',
      blockers: [],
      missingInfo: [],
      nextBestAction: t('onboardingIntake.defaultNextAction', 'Confirm mobilisation plan and assign onboarding team'),
      renewalDate: '',
      targetGoLive: form.targetGoLive,
      sectionStatuses: {
        customerProfile: { status: 'Complete' },
        commercialModel: { status: 'Complete' },
        contractsTerms: { status: 'NotStarted' },
        sites: { status: 'NotStarted' },
        contacts: { status: 'NotStarted' },
        reportingRequirements: { status: 'Complete' },
        mobilisationPlan: { status: 'NotStarted' },
      },
      contract: {
        contractRef: '', startDate: '', endDate: '', noticePeriod: '', renewalTimer: '', status: 'Active', slas: [], obligations: [], milestones: [],
      },
      commercial: {
        model: form.operatingModel,
        contractValue: form.budget,
        pricingBasis: form.pricingModel,
        managementFee: '',
        rebate: '',
        billingRules: '',
        paymentTerms: '',
        margin: '',
        approvalStatus: 'InReview',
        assumptions: [],
      },
      sites: [],
      contacts: [],
      mobilisation: [],
      reporting: form.reportingRequirements.map((reportType) => ({
        reportType, frequency: '', format: '', nextDue: '',
      })),
      activity: [
        { date: new Date().toISOString().slice(0, 10), actor: form.owner || 'System', action: 'Client onboarding intake submitted' },
      ],
      serviceRequirements: {
        userTypes: form.userTypes,
        serviceTimes: form.serviceTimes,
        menus: form.menus,
        dietaryRequirements: form.dietaryRequirements,
        allergens: form.allergens,
        hospitalityNeeds: form.hospitalityNeeds,
        budget: form.budget,
        pricingModel: form.pricingModel,
        complianceExpectations: form.complianceExpectations,
        reportingRequirements: form.reportingRequirements,
      },
    });

    setShowSuccess(true);
    setTimeout(() => navigate(`/customer-management/onboarding/${id}`), 800);
  };

  return (
    <PageArea>
      <BackButton
        handleClick={() => navigate('/customer-management/customers')}
        title={t('backToCustomers', 'Back to All Clients')}
        isLink={false}
        styles={{ marginBottom: '20px' }}
      />

      <PageTitle>{t('onboardingIntake.title', 'New Client Onboarding')}</PageTitle>
      <Typography variant="textSM"
        color="text.secondary"
        sx={{ mb: 4, display: 'block' }}>
        {t('onboardingIntake.subtitle', 'Capture client profile, service & catering requirements, and commercial/compliance expectations to kick off onboarding.')}
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          value={OnboardingIntakeTab.CUSTOMER_PROFILE}
          label={t('onboardingIntake.tabCustomerProfile', 'Client Profile')}
          icon={isTabComplete(OnboardingIntakeTab.CUSTOMER_PROFILE) ? <CheckCircle fontSize="small"
            color="success" /> : undefined}
          iconPosition="end"
        />
        <Tab
          value={OnboardingIntakeTab.SERVICE_REQUIREMENTS}
          label={t('onboardingIntake.tabServiceRequirements', 'Service & Catering Requirements')}
          icon={isTabComplete(OnboardingIntakeTab.SERVICE_REQUIREMENTS) ? <CheckCircle fontSize="small"
            color="success" /> : undefined}
          iconPosition="end"
        />
        <Tab
          value={OnboardingIntakeTab.COMMERCIAL_COMPLIANCE}
          label={t('onboardingIntake.tabCommercialCompliance', 'Commercial & Compliance')}
          icon={isTabComplete(OnboardingIntakeTab.COMMERCIAL_COMPLIANCE) ? <CheckCircle fontSize="small"
            color="success" /> : undefined}
          iconPosition="end"
        />
        <Tab
          value={OnboardingIntakeTab.REVIEW}
          label={t('onboardingIntake.tabReview', 'Review & Submit')}
        />
      </Tabs>

      <Box sx={{ border: '1px solid', borderColor: theme.palette.custom.gray[300], borderRadius: '12px' }}>
        {activeTab === OnboardingIntakeTab.CUSTOMER_PROFILE && (
          <OnboardingCustomerProfile formData={form}
            handleChange={handleChange} />
        )}
        {activeTab === OnboardingIntakeTab.SERVICE_REQUIREMENTS && (
          <OnboardingServiceRequirements formData={form}
            handleTagsChange={handleChange}
            handleChange={handleChange} />
        )}
        {activeTab === OnboardingIntakeTab.COMMERCIAL_COMPLIANCE && (
          <OnboardingCommercialCompliance formData={form}
            handleChange={handleChange}
            handleTagsChange={handleChange} />
        )}
        {activeTab === OnboardingIntakeTab.REVIEW && <OnboardingReview formData={form} />}

        <Stack direction="row"
          justifyContent="space-between"
          sx={{ p: 6, pt: 4 }}>
          <Button
            disabled={activeTab === OnboardingIntakeTab.CUSTOMER_PROFILE}
            onClick={() => setActiveTab(TAB_ORDER[TAB_ORDER.indexOf(activeTab) - 1])}
          >
            {t('onboardingIntake.back', 'Back')}
          </Button>
          {activeTab === OnboardingIntakeTab.REVIEW ? (
            <Button
              variant="primary"
              disabled={!allComplete}
              onClick={handleCreateCustomer}
            >
              {t('onboardingIntake.createCustomer', 'Create client')}
            </Button>
          ) : (
            <Button
              variant="primary"
              endIcon={<NavigateNext />}
              onClick={() => setActiveTab(TAB_ORDER[TAB_ORDER.indexOf(activeTab) + 1])}
            >
              {t('onboardingIntake.continue', 'Save & Continue')}
            </Button>
          )}
        </Stack>
      </Box>

      <MessagesSnackbar
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        message={t('onboardingIntake.successMessage', 'Customer created. Opening workspace...')}
        severity="success"
        duration={3000}
      />
    </PageArea>
  );
};

export default OnboardingIntake;
